import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User as UserIcon,
  Heart,
  Settings,
  LogOut,
  FileEdit,
  ChevronDown,
  Calendar } from
'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
export function UserMenu() {
  const { user, signOut } = useAuth();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  if (!user) {
    return (
      <Link
        to="/auth/signin"
        className={`inline-flex items-center gap-1.5 text-sm font-bold text-white bg-sea-blue hover:bg-terracotta transition-colors px-4 py-2 rounded-full shadow-sm ${isAr ? 'font-arabic' : ''}`}>
        
        <UserIcon size={14} strokeWidth={2.5} />
        {isAr ? 'تسجيل الدخول' : 'Sign in'}
      </Link>);

  }
  const initials = (user.displayName || user.email).
  split(' ').
  map((n) => n[0]).
  slice(0, 2).
  join('').
  toUpperCase();
  const menuItems = [
  {
    icon: UserIcon,
    label: isAr ? 'صفحتي' : 'Your Profile',
    to: `/u/${user.username}`
  },
  {
    icon: Heart,
    label: isAr ? 'مفضلاتي' : 'Your Favorites',
    to: '/favorites'
  },
  {
    icon: Calendar,
    label: isAr ? 'خططك' : 'Your Plans',
    to: '/account/plans'
  },
  {
    icon: FileEdit,
    label: isAr ? 'مساهماتي' : 'Your Contributions',
    to: '/account/contributions'
  },
  {
    icon: Settings,
    label: isAr ? 'الإعدادات' : 'Settings',
    to: '/account/settings'
  }];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 group"
        aria-label={isAr ? 'قائمة المستخدم' : 'User menu'}
        aria-expanded={open}
        aria-haspopup="menu">
        
        {user.avatar ?
        <img
          src={user.avatar}
          alt={user.displayName}
          className="w-9 h-9 rounded-full object-cover border-2 border-white shadow-sm group-hover:border-terracotta transition-colors" /> :


        <div className="w-9 h-9 rounded-full bg-terracotta text-white flex items-center justify-center font-bold text-sm shadow-sm">
            {initials}
          </div>
        }
        <ChevronDown
          size={14}
          className={`text-sea-blue/40 transition-transform ${open ? 'rotate-180' : ''}`} />
        
      </button>

      <AnimatePresence>
        {open &&
        <motion.div
          initial={{
            opacity: 0,
            y: -8
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          exit={{
            opacity: 0,
            y: -8
          }}
          transition={{
            duration: 0.15
          }}
          className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-float border border-sea-blue/10 py-2 z-50 overflow-hidden">
          
            <div className="px-4 py-3 border-b border-sea-blue/5 flex items-center gap-3">
              {user.avatar ?
            <img
              src={user.avatar}
              alt={user.displayName}
              className="w-10 h-10 rounded-full object-cover" /> :


            <div className="w-10 h-10 rounded-full bg-terracotta text-white flex items-center justify-center font-bold text-sm">
                  {initials}
                </div>
            }
              <div className="min-w-0 flex-grow">
                <div
                className={`font-bold text-sea-blue truncate ${isAr ? 'font-arabic' : ''}`}>
                
                  {user.displayName}
                </div>
                <div className="text-xs text-sea-blue/80 truncate ltr-numerals">
                  {user.email}
                </div>
              </div>
            </div>

            {menuItems.map((item) =>
          <button
            key={item.to}
            onClick={() => {
              setOpen(false);
              navigate(item.to);
            }}
            className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-sea-blue hover:bg-sand/40 transition-colors">
            
                <item.icon size={16} className="text-sea-blue/50" />
                <span className={isAr ? 'font-arabic' : ''}>{item.label}</span>
              </button>
          )}

            <div className="border-t border-sea-blue/5 mt-1 pt-1">
              <button
              onClick={() => {
                setOpen(false);
                signOut();
                navigate('/');
              }}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-terracotta hover:bg-terracotta/5 transition-colors">
              
                <LogOut size={16} />
                <span className={isAr ? 'font-arabic' : ''}>
                  {isAr ? 'تسجيل الخروج' : 'Sign out'}
                </span>
              </button>
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </div>);

}